import ApiService from "../../../services/ApiService";
import { resolveBackendApiUrl } from "../../../services/ApiBaseUrl";
import { apiFetch } from "../../../services/apiFetch";

async function getFacebook(path) {
    await ApiService.getAccessToken();
    const headers = new Headers(ApiService.init.headers);
    headers.set("Accept", "application/json");

    const url = resolveBackendApiUrl(path);
    const result = await apiFetch(url, {
        method: "GET",
        headers,
        cache: "no-store",
    });

    if (!result.ok) {
        throw new Error(`GET ${url} failed (HTTP ${result.status})`);
    }

    return result.json();
}

/** POST /test/facebook/* — Bearer JWT only, no CSRF. */
async function postFacebook(path, body) {
    await ApiService.getAccessToken();
    const headers = new Headers(ApiService.init.headers);
    headers.set("Content-Type", "application/json");
    headers.set("Accept", "application/json");

    const url = resolveBackendApiUrl(path);
    const result = await apiFetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
    });

    const json = await result.json().catch(() => null);
    if (!result.ok) {
        const detail = json?.message || json?.error || `HTTP ${result.status}`;
        throw new Error(typeof detail === "string" ? detail : `POST ${url} failed`);
    }
    // Backend often returns HTTP 200 with { code: "FAILURE", message }
    if (json && json.code === "FAILURE") {
        throw new Error(json.message || "Facebook request failed");
    }
    return json;
}

const FacebookTestService = {
    getStatus: () => getFacebook("/test/facebook/status"),

    /**
     * Standalone image for a test post.
     * @param {{ imageSource?: string, excludeStoryId?: string, storyId?: string }} [opts]
     */
    getPreview: (opts = {}) => {
        const params = new URLSearchParams();
        const imageSource = typeof opts === "string" ? opts : opts.imageSource;
        const excludeStoryId = typeof opts === "string" ? undefined : opts.excludeStoryId;
        const storyId = typeof opts === "string" ? undefined : opts.storyId;
        if (typeof imageSource === "string" && imageSource.trim()) {
            params.set("imageSource", imageSource.trim());
        }
        if (typeof excludeStoryId === "string" && excludeStoryId.trim()) {
            params.set("excludeStoryId", excludeStoryId.trim());
        }
        if (typeof storyId === "string" && storyId.trim()) {
            params.set("storyId", storyId.trim());
        }
        const query = params.toString();
        return getFacebook(`/test/facebook/preview${query ? `?${query}` : ""}`);
    },

    /**
     * Unpublished standalone packs to cycle in Test Facebook.
     * @param {{ imageSource?: string, limit?: number }} [opts]
     */
    getCandidates: (opts = {}) => {
        const params = new URLSearchParams();
        if (typeof opts.imageSource === "string" && opts.imageSource.trim()) {
            params.set("imageSource", opts.imageSource.trim());
        }
        if (Number.isFinite(opts.limit) && opts.limit > 0) {
            params.set("limit", String(Math.trunc(opts.limit)));
        }
        params.set("_t", String(Date.now()));
        return getFacebook(`/test/facebook/candidates?${params.toString()}`);
    },

    /**
     * Next unpublished pack (POST so CDN/browser cannot reuse GET /preview).
     * @param {{ imageSource?: string, excludeStoryId?: string, storyId?: string }} [opts]
     */
    nextCandidate: (opts = {}) => {
        const payload = {};
        if (typeof opts.imageSource === "string" && opts.imageSource.trim()) {
            payload.imageSource = opts.imageSource.trim();
        }
        if (typeof opts.excludeStoryId === "string" && opts.excludeStoryId.trim()) {
            payload.excludeStoryId = opts.excludeStoryId.trim();
        }
        if (typeof opts.storyId === "string" && opts.storyId.trim()) {
            payload.storyId = opts.storyId.trim();
        }
        return postFacebook("/test/facebook/next-candidate", payload);
    },

    /**
     * Publish a public HTTPS image URL to the Page.
     * @param {string} imageUrl
     * @param {string} [caption]
     * @param {{ published?: boolean }} [opts]
     */
    postImageUrl: (imageUrl, caption, opts = {}) => {
        const payload = {
            imageUrl: imageUrl.trim(),
            published: opts.published !== false,
        };
        const trimmedCaption = typeof caption === "string" ? caption.trim() : "";
        if (trimmedCaption) {
            payload.caption = trimmedCaption;
        }
        return postFacebook("/test/facebook/post-url", payload);
    },

    /**
     * Post puzzle PNG to the Facebook Page.
     * @param {string} imageBase64 data URL or raw base64
     * @param {string} caption
     * @param {{ published?: boolean, fileName?: string }} [opts]
     *   Default published=false → unpublished (Page admins only / “Only me”).
     */
    postImage: (imageBase64, caption, opts = {}) => {
        const payload = {
            imageBase64,
            published: opts.published === true,
        };
        const trimmedCaption = typeof caption === "string" ? caption.trim() : "";
        if (trimmedCaption) {
            payload.caption = trimmedCaption;
        }
        if (opts.fileName) {
            payload.fileName = opts.fileName;
        }
        return postFacebook("/test/facebook/post-image", payload);
    },
};

export default FacebookTestService;
